import type { Stripe, StripeElements } from '@stripe/stripe-js';

import { CACHE_TIME } from '@/const/cache';
import { SignupState } from '@/types/Signup';

type SetupIntentResponse = {
  clientSecret: string;
  customerId: string;
  setupIntentId: string;
};

type ConfirmArgs = {
  stripe: Stripe;
  elements: StripeElements;
  /** The applicant, with billing already mirrored from the shipping address. */
  applicant: SignupState;
  isWallet?: boolean;
  /**
   * With a wallet: send the applicant's own billing address instead of the one
   * in the wallet sheet. Set after Stripe refused the wallet's address, so the
   * member can correct it.
   */
  useApplicantBilling?: boolean;
};

/**
 * Creates the customer and SetupIntent, then confirms whichever payment element
 * is mounted. Resolves to the SetupIntent id, or throws with a message fit to
 * show the applicant.
 *
 * Shared by both payment paths — the card fields and the wallet buttons — because
 * the only difference between them is which element collected the details.
 * Nothing exists in Stripe until this runs, so an applicant who abandons the
 * form costs nothing.
 */
export async function confirmSetupIntent({
  stripe,
  elements,
  applicant,
  isWallet = false,
  useApplicantBilling = false
}: ConfirmArgs): Promise<string> {
  // Required first in the deferred flow: runs the element's own validation and
  // surfaces incomplete fields inline, before anything is created in Stripe.
  const { error: submitError } = await elements.submit();

  if (submitError) {
    throw new Error(
      submitError.message || 'Please check your payment details.'
    );
  }

  const response = await fetch('/api/signup/setup-intent', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    next: { revalidate: CACHE_TIME.NO_CACHE },
    body: JSON.stringify({ user: applicant })
  });

  if (!response.ok) {
    throw new Error('We could not start payment. Please try again.');
  }

  const { clientSecret, setupIntentId } =
    (await response.json()) as SetupIntentResponse;

  const { error } = await stripe.confirmSetup({
    elements,
    clientSecret,
    confirmParams: {
      // Required by Stripe whenever the Elements group was created without an
      // explicit `paymentMethodTypes` list (ours is, so it resolves methods
      // automatically) — it's the fallback for methods that must redirect,
      // even though `redirect: 'if_required'` below means card and wallets
      // never actually use it.
      return_url: window.location.href,
      // Supplied because the card element is told not to collect them
      // (`billingDetails: 'never'`); Stripe rejects the confirm otherwise. A
      // wallet must not send them: they would override its own address, which
      // is only wanted once that address has been refused.
      ...((!isWallet || useApplicantBilling) && {
        payment_method_data: {
          billing_details: {
            name: `${applicant.firstName} ${applicant.lastName}`.trim(),
            email: applicant.email,
            phone: applicant.phone,
            address: {
              line1: applicant.billingAddress,
              line2: applicant.billingAptUnitSuite || undefined,
              city: applicant.billingCity,
              state: applicant.billingState,
              postal_code: applicant.billingPostalCode,
              country: 'US'
            }
          }
        }
      })
    },
    // Keeps the applicant on the page. Stripe still redirects for methods that
    // genuinely require it; cards and wallets do not.
    redirect: 'if_required'
  });

  if (error) {
    throw new Error(error.message || 'We could not save your payment method.');
  }

  return setupIntentId;
}
